import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import { api } from "./api";
import { Badge, BoutonSombre, Eyebrow } from "./ui";
import { useConfirmation } from "./Confirmation";

/* Contrôle de l'import : après « Importer une sauvegarde », le serveur compare le FICHIER à
   ce que la base contient (collection par collection, et les chiffres de l'école) et garde
   le rapport. Le fichier n'est pas gardé : seulement son empreinte, contre laquelle le
   contrôle se relance à tout moment. Rouge = il manque quelque chose (avec qui) ; ambre =
   la base en a plus (l'activité depuis l'import, c'est normal). */

const STATUTS = {
  ok: { ton: "vert", label: "OK" },
  manque: { ton: "rouge", label: "MANQUE" },
  plus: { ton: "ambre", label: "EN PLUS" },
  ignoree: { ton: "bleu", label: "LAISSÉE DE CÔTÉ" },
  hors_fichier: { ton: "bleu", label: "PAS DANS LE FICHIER" },
};
const quand = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso
    : d.toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const chf = (n) => (n === null || n === undefined ? "—"
  : `${new Intl.NumberFormat("fr-CH", { maximumFractionDigits: 2 }).format(n)} CHF`);

export default function ControleImport() {
  const { informer } = useConfirmation();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [tout, setTout] = useState(false);

  const charger = useCallback(async () => {
    try { setData(await api("/controle-import")); setErreur(""); } catch (e) { setErreur(e.message); }
  }, []);
  useEffect(() => {
    charger();
    // L'import vient de se terminer (Sauvegarde de la console) : son contrôle est prêt.
    const maj = () => charger();
    window.addEventListener("controle-import-maj", maj);
    return () => window.removeEventListener("controle-import-maj", maj);
  }, [charger]);

  const relancer = async () => {
    setEnCours(true);
    try {
      await api("/controle-import/relancer", { method: "POST" });
      await charger();
    } catch (e) {
      await informer({ titre: "Contrôle impossible", ton: "danger", texte: e.message });
    }
    setEnCours(false);
  };

  if (erreur) return <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>;
  if (!data) return <p className="text-sm text-[var(--muted)]">Chargement…</p>;
  const c = data.controle;
  if (!c) {
    return (
      <div className="bg-white rounded-2xl p-5 border text-sm text-[var(--muted)]" style={{ borderColor: "var(--border)" }}
        data-testid="controle-import-vide">
        Aucun import dans cette console pour l'instant. Le contrôle se fait tout seul à chaque
        « Importer une sauvegarde » (juste au-dessus) : il compare le fichier à ce que la console
        contient ensuite, et dit ce qui manquerait.
      </div>
    );
  }
  const vert = c.statut === "ok";
  const collections = c.collections.filter((x) => x.statut !== "ignoree");
  const visibles = tout ? collections : collections.filter((x) => x.statut !== "ok");
  const ignorees = c.collections.filter((x) => x.statut === "ignoree");
  // Les chiffres dont la collection n'est pas dans le fichier : une seule ligne en bas.
  const chiffres = c.chiffres.filter((ch) => ch.statut !== "hors_fichier");
  const horsFichier = c.chiffres.filter((ch) => ch.statut === "hors_fichier");
  return (
    <div className="bg-white rounded-2xl p-5 border space-y-5" style={{ borderColor: "var(--border)" }}
      data-testid="controle-import">
      <div className="flex flex-wrap items-start gap-3">
        <div className="text-sm min-w-0">
          <div className="font-semibold break-words">{data.fichier || "Sauvegarde"}</div>
          <div className="text-xs text-[var(--muted)]">
            Sauvegarde du {quand(data.sauvegarde_du)} · importée le {quand(data.le)}
            {data.par ? ` par ${data.par}` : ""} · {data.mode === "remplacer" ? "base remplacée" : "base complétée"}
            {data.anonymisee ? " · copie anonymisée" : ""}
          </div>
          <div className="text-xs text-[var(--muted)]">Contrôle fait le {quand(c.le)}{c.par ? ` (${c.par})` : ""}</div>
        </div>
        <div className="ml-auto">
          <BoutonSombre onClick={relancer} disabled={enCours} testid="controle-import-relancer">
            <RefreshCw size={14} className={enCours ? "animate-spin" : ""} /> {enCours ? "Contrôle…" : "Relancer le contrôle"}
          </BoutonSombre>
        </div>
      </div>

      <div className="rounded-2xl px-4 py-3 text-sm flex gap-2" data-testid="controle-import-resume"
        style={vert ? { background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }
          : { background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }}>
        {vert ? <CheckCircle2 size={18} className="shrink-0" /> : <AlertTriangle size={18} className="shrink-0" />}
        <span>{c.resume}</span>
      </div>
      <p className="text-[11px] text-[var(--muted)]">
        Juste après l'import, tout doit être vert. Plus tard, l'activité de l'école ajoute des
        documents (ambre, « en plus ») : c'est normal. Seul le rouge dit qu'il manque quelque
        chose du fichier.
      </p>

      <div className="space-y-2">
        <Eyebrow>Les chiffres de l'école — fichier → console</Eyebrow>
        <ul className="divide-y border rounded-2xl overflow-hidden" style={{ borderColor: "var(--border)" }}>
          {chiffres.map((ch) => (
            <li key={ch.cle} className="px-4 py-3 text-sm" data-testid={`controle-chiffre-${ch.cle}`}
              style={{ background: ch.statut === "manque" ? "#FEF2F2" : undefined }}>
              <div className="flex flex-wrap items-center gap-2">
                <Badge ton={(STATUTS[ch.statut] || STATUTS.ok).ton}>{(STATUTS[ch.statut] || STATUTS.ok).label}</Badge>
                <span className="font-medium">{ch.libelle}</span>
              </div>
              {ch.groupes.length === 0 && <div className="text-xs text-[var(--muted)] mt-1">aucun, des deux côtés</div>}
              {ch.groupes.map((g) => (
                <div key={g.groupe} className="mt-1 text-xs">
                  <div className="flex flex-wrap gap-x-3">
                    {g.groupe && <span className="text-[var(--muted)]">{g.groupe} :</span>}
                    <span>fichier <strong>{g.fichier ?? "—"}</strong>{ch.montant ? ` (${chf(g.total_fichier)})` : ""}</span>
                    <span>→ console <strong style={{ color: g.statut === "manque" ? "#B91C1C" : undefined }}>
                      {g.base}</strong>{ch.montant ? ` (${chf(g.total_base)})` : ""}</span>
                  </div>
                  <Manquants nb={g.nb_manquants} liste={g.manquants} />
                </div>
              ))}
              {ch.note && <div className="text-[11px] text-[var(--muted)] mt-1">{ch.note}</div>}
            </li>
          ))}
        </ul>
        {horsFichier.length > 0 && (
          <p className="text-[11px] text-[var(--muted)]" data-testid="controle-hors-fichier">
            Pas dans ce fichier (rien à comparer) :{" "}
            {horsFichier.map((ch) => {
              const base = ch.groupes.reduce((n, g) => n + (g.base || 0), 0);
              return `${ch.libelle}${base ? ` (${base} dans la console)` : ""}`;
            }).join(" · ")}.
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3">
          <Eyebrow>Collection par collection</Eyebrow>
          <button type="button" className="text-xs underline text-[var(--muted)] ml-auto"
            onClick={() => setTout(!tout)} data-testid="controle-import-toutes">
            {tout ? "Seulement les écarts" : `Voir les ${collections.length} collections`}
          </button>
        </div>
        {visibles.length === 0 ? (
          <p className="text-xs text-[var(--muted)]">Aucun écart : chaque collection a, dans la console, tous les documents du fichier.</p>
        ) : (
          <ul className="divide-y border rounded-2xl overflow-hidden" style={{ borderColor: "var(--border)" }}>
            {visibles.map((x) => (
              <li key={x.collection} className="px-4 py-2.5 text-sm" data-testid="controle-collection"
                style={{ background: x.statut === "manque" ? "#FEF2F2" : undefined }}>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge ton={(STATUTS[x.statut] || STATUTS.ok).ton}>{(STATUTS[x.statut] || STATUTS.ok).label}</Badge>
                  <code className="text-xs break-all">{x.collection}</code>
                  <span className="text-xs text-[var(--muted)] ml-auto">
                    fichier {x.dans_le_fichier} → console {x.dans_la_base}
                    {x.en_plus ? ` · ${x.en_plus} en plus` : ""}
                  </span>
                </div>
                <Manquants nb={(x.nb_manquants || 0) + (x.manque_sans_identifiant || 0)} liste={x.manquants} />
              </li>
            ))}
          </ul>
        )}
      </div>

      {ignorees.length > 0 && (
        <div className="space-y-1">
          <Eyebrow>Laissées de côté par l'import (voulu)</Eyebrow>
          <ul className="text-xs space-y-1">
            {ignorees.map((x) => (
              <li key={x.collection} className="break-words">
                <code>{x.collection}</code> ({x.dans_le_fichier}) — <span className="text-[var(--muted)]">{x.ignoree}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {c.absentes_du_fichier?.length > 0 && (
        <div className="space-y-1">
          <Eyebrow>Dans la console, pas dans le fichier</Eyebrow>
          <ul className="text-xs space-y-1">
            {c.absentes_du_fichier.map((x) => (
              <li key={x.collection} className="break-words">
                <code>{x.collection}</code> ({x.dans_la_base}) — <span className="text-[var(--muted)]">{x.raison}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {data.imports?.length > 1 && (
        <div className="text-[11px] text-[var(--muted)]">
          Imports précédents : {data.imports.slice(1).map((i) => `${quand(i.le)} (${i.fichier || "—"}, ${i.mode})`).join(" · ")}
        </div>
      )}
    </div>
  );
}

function Manquants({ nb, liste }) {
  if (!nb) return null;
  return (
    <div className="text-xs mt-1" style={{ color: "#B91C1C" }} data-testid="controle-manquants">
      Manque{nb > 1 ? "nt" : ""} {nb} :{" "}
      {(liste || []).map((m) => (m.repere ? `${m.id} (${m.repere})` : m.id)).join(", ")}
      {nb > (liste || []).length ? `, et ${nb - (liste || []).length} autre(s)` : ""}
    </div>
  );
}
